import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';

const TeamContext = createContext({
    teamStatus: 'none', // 'none' | 'pending' | 'approved'
    team: null,
    loading: true,
    error: null,
    role: null,
    permissions: [],
    plan: 'free',
    refreshTeamStatus: () => { },
    joinTeam: async (code) => { },
    hasPermission: (permission) => false,
});

export const useTeam = () => {
    const context = useContext(TeamContext);
    if (!context) {
        throw new Error('useTeam must be used within TeamProvider');
    }
    return context;
};

export const TeamProvider = ({ children }) => {
    const { user } = useAuth();
    const [teamStatus, setTeamStatus] = useState('none');
    const [team, setTeam] = useState(null);
    const [role, setRole] = useState(null);
    const [permissions, setPermissions] = useState([]);
    const [plan, setPlan] = useState('free');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const refreshTeamStatus = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await api.get('/teams/join-requests/me');
            const data = response.data;

            setTeamStatus(data.status);

            if (data.status === 'approved' || data.status === 'pending') {
                setTeam({
                    id: data.team_id,
                    name: data.team_name,
                    requestId: data.request_id,
                });
                setRole(data.role);
                setPermissions(data.permissions || []);
                setPlan(data.plan || 'free');
            } else {
                setTeam(null);
                setRole(null);
                setPermissions([]);
                setPlan('free');
            }
        } catch (err) {
            console.error('Error fetching team status:', err);
            setError(err.response?.data?.message || 'Failed to fetch team status');
            setTeamStatus('none');
            setTeam(null);
        } finally {
            setLoading(false);
        }
    };

    const joinTeam = async (teamCode) => {
        try {
            setError(null);

            const response = await api.post('/teams/join-requests', {
                team_code: teamCode,
            });

            const data = response.data;

            // Update state with pending request
            setTeamStatus('pending');
            setTeam({
                id: data.team_id,
                name: data.team_name,
                requestId: data.request_id,
            });

            return {
                success: true,
                message: data.message,
                data,
            };
        } catch (err) {
            const errorCode = err.response?.data?.detail?.code;
            const errorMessage = err.response?.data?.detail?.message || 'Failed to join team';

            setError(errorMessage);

            return {
                success: false,
                error: errorMessage,
                errorCode,
            };
        }
    };

    // Load team status when user changes
    useEffect(() => {
        if (user) {
            refreshTeamStatus();
        } else {
            setTeamStatus('none');
            setTeam(null);
            setRole(null);
            setPermissions([]);
            setPlan('free');
            setLoading(false);
        }
    }, [user]);

    const hasPermission = (permission) => {
        if (user?.role === 'admin') return true;
        if (role === 'coach') return true;
        return permissions.includes(permission);
    };

    const value = {
        teamStatus,
        team,
        role,
        permissions,
        plan,
        loading,
        error,
        refreshTeamStatus,
        joinTeam,
        hasPermission,
    };

    return <TeamContext.Provider value={value}>{children}</TeamContext.Provider>;
};

export default TeamContext;
